/**
 * Protocol Cell Renderer
 *
 * With PR #3413, displays service names from structured Port models.
 * This shows the actual service that matched the filter (e.g., "https", "ssh", "ftp").
 */

import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import type { Asset } from '@/types';

interface ProtocolCellProps {
  asset: Asset;
  maxVisible?: number;
  wrapInTooltip?: boolean;
}

/**
 * ProtocolCell - Displays protocol/service names with "+N" counter and tooltip
 *
 * With PR #3413, service data comes from structured Port models via HAS_PORT relationships.
 * Each Port has a 'service' field (e.g., "https", "ssh") that identifies the protocol.
 *
 * Display modes:
 * - Table mode (maxVisible=1): Shows first service with "+N" counter
 * - Full display (maxVisible=Infinity): Shows all services inline (default, backwards compatible)
 *
 * Features:
 * - Hover anywhere in cell to see all services in tooltip (when wrapInTooltip=true)
 * - Services sorted alphabetically, deduplicated
 * - Matches AttackSurfaceComponent pattern
 *
 * @param asset - The asset to render services for
 * @param maxVisible - Maximum number of chips to display before "+N" counter (default: Infinity)
 * @param wrapInTooltip - Whether to wrap entire cell in tooltip (default: false)
 * @returns Service chip display or dash if not available
 */
export const ProtocolCell: React.FC<ProtocolCellProps> = React.memo(
  ({ asset, maxVisible = Infinity, wrapInTooltip = false }) => {
    const services = useMemo(() => {
      // Service data now comes from structured Port models
      if (
        'ports' in asset &&
        Array.isArray(asset.ports) &&
        asset.ports.length > 0
      ) {
        return asset.ports
          .map(port => port.service)
          .filter(Boolean)
          .filter((service, index, self) => self.indexOf(service) === index) // unique
          .sort();
      }

      // Fallback for backwards compatibility with old Attribute model
      if ('attributes' in asset && Array.isArray(asset.attributes)) {
        const protocolAttr = asset.attributes.find(
          attr => attr.name !== 'port' && attr.name !== 'technology'
        );
        if (protocolAttr?.name) {
          return [protocolAttr.name];
        }
      }

      return [];
    }, [asset]);

    if (services.length === 0) {
      return <>-</>;
    }

    return (
      <ChipsList
        items={services}
        maxVisible={maxVisible}
        size="xs"
        wrapInTooltip={wrapInTooltip}
        data-testid="protocol-chips"
      />
    );
  }
);

ProtocolCell.displayName = 'ProtocolCell';
